import { Container, IconButton, Typography } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import { Skeleton } from '@material-ui/lab'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Button from '@/components/button'
import { useCreateReportResolution } from '@/hooks/mutations/use-create-report-resolution'
import { useGetCogUrlFromDiagnosis } from '@/hooks/queries/use-get-cog-url-from-diagnosis'
import { useGetDiagnosisById } from '@/hooks/queries/use-get-diagnosis-by-id'
import { useGetLatestDiagnosisReport } from '@/hooks/queries/use-get-latest-diagnosis-report'
import { useGetPerimeterData } from '@/hooks/queries/use-get-perimeter-data'
import { useGetProjectByID } from '@/hooks/queries/use-get-project-by-id'
import { useAuth } from '@/hooks/use-auth'
import DemandResume from '@/pages/demands/steps/resume/components/demand-resume'
import { routes } from '@/routes/config'

import { AcceptanceTermModal } from '../../components/acceptance-term-modal'
import { CancelModal } from '../../components/cancel-modal'
import { Map } from '../../components/map'
import { ReportIssues } from '../../components/report-issues'
import { useDemandAnalysisStore } from '../../contexts/demand-analysis-store-context'
import { useDemandAnalysisRoute } from '../../hooks/use-demand-analysis-route'
import { useStyles } from './styles'

export function Info() {
  const classes = useStyles()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [isAcceptanceTermOpen, setIsAcceptanceTermOpen] = useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  const { treatment, culture, diagnosisId, makeUrl } = useDemandAnalysisRoute()
  const resetStore = useDemandAnalysisStore((state) => state.reset)

  const { data: diagnosis } = useGetDiagnosisById(
    treatment!,
    culture!,
    diagnosisId,
  )

  const { data: projects } = useGetProjectByID(diagnosis?.project.id)
  const { data: perimeterData } = useGetPerimeterData(diagnosisId!)
  const { data: cogUrl } = useGetCogUrlFromDiagnosis(culture!, diagnosisId!)
  const { data: report } = useGetLatestDiagnosisReport(
    treatment!,
    culture!,
    diagnosisId!,
  )

  const { mutate: createReportResolution } = useCreateReportResolution(
    treatment!,
    culture!,
    diagnosisId!,
    report?.id!,
  )

  function handleSelectFiles() {
    navigate(makeUrl('select_files'))
  }

  function handleProceedReport() {
    if (!report) return

    createReportResolution(
      { resolution: 'proceed', resolved_by: user.id },
      {
        onSuccess: () => {
          navigate(makeUrl('finish'))
        },
      },
    )
  }

  function handleCancelReport(): void {
    if (!report) return

    createReportResolution(
      { resolution: 'cancel', resolved_by: user.id },
      {
        onSuccess: () => {
          navigate(makeUrl('finish'), {
            state: { cancelled: true },
          })
        },
      },
    )
  }

  function goBack() {
    navigate({
      pathname: routes.Diagnoses(),
      search: '?searchType=status&search=reported',
    })
  }

  const isLoadingData = !diagnosis || !perimeterData || !cogUrl || !report
  const canProceedWithReport = report?.issues.every((issue) =>
    issue.name.startsWith('qa'),
  )

  useEffect(() => {
    resetStore()
  }, [])

  return (
    <Container className={classes.container}>
      <header className={classes.header}>
        <IconButton
          size='small'
          onClick={goBack}
          color='secondary'
          aria-label='Voltar'
        >
          <ArrowBack />
        </IconButton>

        <div>
          <Typography variant='h2'>Informações do diagnóstico</Typography>
          <Typography variant='h5'>
            Analise os pontos de atenção do diagnóstico
          </Typography>
        </div>
      </header>

      <div className={classes.content}>
        <div className={classes.resume}>
          <Typography component={'span'}>Resumo do diagnóstico</Typography>

          <div>
            <ReportIssues report={report} />

            {!diagnosis ? (
              <Skeleton
                role='progressbar'
                width={'100%'}
                height={316}
                variant='rect'
                style={{ marginTop: 10, borderRadius: 4 }}
              />
            ) : (
              <DemandResume
                formData={{
                  area: diagnosis!.perimeter.area,
                  image_type: diagnosis!.imageType,
                  image_count: diagnosis!.imageCount || undefined,
                  culture: diagnosis!.culture,
                  treatment: diagnosis!.treatment,
                  project: diagnosis!.project.name,
                  region: projects?.regions.find(
                    (region) => region.id === diagnosis?.project.region_id,
                  )?.name!,
                  demand_name: diagnosis!.demandName,
                  flight_date: diagnosis!.flightDate,
                  sponsor: diagnosis!.username,
                }}
              />
            )}
          </div>
        </div>
        <div className={classes.map}>
          <Typography component={'span'}>Área cadastrada</Typography>

          <div>
            {!perimeterData || !cogUrl ? (
              <Skeleton
                role='progressbar'
                width={'100%'}
                height={'100%'}
                variant='rect'
                style={{ borderRadius: 4 }}
              />
            ) : (
              <Map
                perimeter={perimeterData}
                cogUrl={cogUrl}
                editableButton
                disabledEditableButton={isLoadingData}
              />
            )}
          </div>
        </div>
      </div>

      <footer className={classes.footer}>
        <Button
          disabled={isLoadingData}
          outlined
          onClick={() => setIsCancelModalOpen(true)}
        >
          Cancelar diagnóstico
        </Button>
        {canProceedWithReport && (
          <Button
            disabled={isLoadingData}
            filled
            onClick={() => setIsAcceptanceTermOpen(true)}
          >
            Seguir com diagnóstico
          </Button>
        )}

        <Button disabled={isLoadingData} onClick={handleSelectFiles}>
          Enviar novos arquivos
        </Button>
      </footer>

      <AcceptanceTermModal
        open={isAcceptanceTermOpen}
        onClose={() => setIsAcceptanceTermOpen(false)}
        onSubmit={handleProceedReport}
      />

      <CancelModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onSubmit={handleCancelReport}
        diangosisName={diagnosis?.demandName}
      />
    </Container>
  )
}
